import { useEffect, useMemo, useState } from 'react'

import { encode, decode } from 'js-base64'

import {
  Space,
  SpaceListOrderByEnum,
  SpaceType,
} from '@tribeplatform/gql-client/types'
import { SpaceAnalyticsModal } from '@tribeplatform/react-components/Analytics/SpaceAnalytics'
import { AuditLogsModal } from '@tribeplatform/react-components/AuditLog'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNavigate } from '@tribeplatform/react-components/Link'
import {
  SpaceActionItems,
  SpaceSettingsModal,
} from '@tribeplatform/react-components/Space'
import { useRouter } from '@tribeplatform/react-sdk'
import { useSpaces } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Button, IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { useQuery } from '../../../hooks/useQuery.js'
import { useDebounce } from '../../../utils/useDebounce.js'
import { Table } from '../../Table/index.js'

export const Spaces = () => {
  let initialState = {
    orderBy: {
      column: SpaceListOrderByEnum.CREATED_AT,
      reverse: true,
    },
    query: null,
    filterBy: [],
  }
  const { $t } = useI18n()
  const navigate = useNavigate()

  const { replace } = useRouter()
  const { state: queryState } = useQuery()
  if (queryState) {
    try {
      initialState = { ...initialState, ...JSON.parse(decode(queryState)) }
    } catch (error) {
      logger.error('Could not parse initial state for members')
    }
  }

  const [state, setState] = useState(initialState)
  const debouncedQuery = useDebounce(state?.query, 300)

  useEffect(() => {
    replace(`?state=${encode(JSON.stringify(state))}`)
  }, [replace, state])

  const [editSpaceModal, setEditSpaceModal] = useState<string | null>()
  const [auditLogSpaceId, setAuditLogsSpaceId] = useState<string>()
  const [spaceAnalyticsModal, setSpaceAnalyticsModal] = useState<Space>()

  const {
    data,
    isInitialLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useSpaces({
    fields: {
      image: 'basic',
      collection: 'basic',
      postsCount: 'basic',
      authMemberProps: 'all',
    },
    variables: {
      limit: 15,
      query: debouncedQuery,
      orderBy: state?.orderBy.column,
      reverse: state?.orderBy.reverse,
      type: [SpaceType.Group],
    },
    useInfiniteQueryOptions: { keepPreviousData: true },
  })

  const spaces = simplifyPaginatedResult<Space>(data)

  const columns = useMemo(
    () => [
      {
        accessor: 'image',
        renderer: 'spaceImage',
        Header: (
          <div className="w-8">
            <SvgIcon className="w-5 h-5 m-auto" name="image" />
          </div>
        ),
        visible: true,
        width: 'collapse',
      },
      {
        Header: $t({ defaultMessage: 'ID', id: 'Generics.ID' }),
        accessor: 'id',
        renderer: 'spacePrimary',
        visible: false,
        width: 'sm',
      },
      {
        Header: $t({ defaultMessage: 'Name', id: 'Generics.Name.Noun' }),
        accessor: 'name',
        renderer: 'spacePrimary',
        visible: true,
        width: 'md',
      },
      {
        Header: $t({
          defaultMessage: 'Members count',
          id: 'Generics.MembersCount',
        }),
        accessor: 'membersCount',
        renderer: 'number',
        visible: true,
        width: 'sm',
      },
      {
        Header: $t({
          defaultMessage: 'Posts count',
          id: 'Generics.PostsCount',
        }),
        accessor: 'postsCount',
        renderer: 'number',
        visible: true,
        width: 'sm',
      },
      {
        Header: $t({ defaultMessage: 'Slug', id: 'Generics.Slug' }),
        accessor: 'slug',
        renderer: 'spacePrimary',
        visible: false,
        width: 'sm',
      },
      {
        Header: $t({ defaultMessage: 'Created at', id: 'Generics.CreatedAt' }),
        accessor: 'createdAt',
        renderer: 'date',
        visible: true,
        orderKey: SpaceListOrderByEnum.CREATED_AT,
        width: 'sm',
      },
      {
        Header: $t({ defaultMessage: 'Private', id: 'Generics.Private' }),
        accessor: 'private',
        renderer: 'boolean',
        visible: true,
        width: 'xs',
      },
      {
        Header: $t({
          defaultMessage: 'Invite only',
          id: 'Generics.InviteOnly',
        }),
        accessor: 'inviteOnly',
        renderer: 'boolean',
        visible: false,
        width: 'xs',
      },
      {
        Header: $t({ defaultMessage: 'Hidden', id: 'Generics.Hidden' }),
        accessor: 'hidden',
        renderer: 'boolean',
        visible: false,
        width: 'xs',
      },
      {
        Header: $t({
          defaultMessage: 'Description',
          id: 'Generics.Description',
        }),
        accessor: 'description',
        renderer: 'longtext',
        visible: false,
        width: 'xl',
      },
      {
        visible: true,
        accessor: 'actions',
        locked: true,
        width: 'collapse',
        renderer: (_, space: Space) => (
          <Dropdown placement="bottom-end">
            <Dropdown.ButtonMinimal as="div">
              <IconButton
                variant="tertiaryNeutral"
                rounded
                icon={<SvgIcon name="dots-horizontal" />}
                size="sm"
              />
            </Dropdown.ButtonMinimal>
            <SpaceActionItems space={space} hideHotKeys />
          </Dropdown>
        ),
      },
    ],
    [$t],
  )

  return (
    <div className="w-full">
      <Table
        storageId="spaces"
        isLoading={isInitialLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        title={$t({ defaultMessage: '{SPACES_CC}', id: 'Generics.Spaces' })}
        state={state}
        onStateChange={setState}
        keyboardControl
        entityType="space"
        actions={
          <Button
            size="lg"
            variant="primary"
            leadingIcon={<SvgIcon name="package-plus" />}
            onClick={() => navigate(`/manage/customizer/create-space`)}
          >
            <T id="Generics.AddSpace" defaultMessage="Add {SPACE}" />
          </Button>
        }
      />

      {editSpaceModal && (
        <SpaceSettingsModal
          open
          onClose={() => setEditSpaceModal(null)}
          space={spaces?.nodes?.find(s => s.id === editSpaceModal)}
        />
      )}
      {auditLogSpaceId && (
        <AuditLogsModal
          open
          onClose={() => setAuditLogsSpaceId(null)}
          filters={{
            spaceId: auditLogSpaceId,
          }}
        />
      )}
      {spaceAnalyticsModal && (
        <SpaceAnalyticsModal
          open
          onClose={() => setSpaceAnalyticsModal(null)}
          space={spaceAnalyticsModal}
        />
      )}
    </div>
  )
}
